'use client';

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from '@/components/shared/Link';
import Image from '@/components/shared/Image';
import { PageHero } from '@/components/shared/PageHero';
import { SectionLabel } from '@/components/shared/SectionLabel';
import { JobCard } from '@/components/shared/JobCard';
import { useTranslations } from '@/i18n/intl';
import { useSettings } from '@/components/shared/SettingsProvider';
import { getJobsList } from '@/lib/actions/jobs';
import NotFoundPage from './NotFoundPage';

export default function LifeAtSimisiPage() {
  const settings = useSettings();
  const isCareerEnabled = settings.enable_career !== 'false';
  const t = useTranslations('Common');
  const tLife = useTranslations('LifeAtSimisi');
  const [jobs, setJobs] = useState<any[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isCareerEnabled) {
      setReady(true);
      return;
    }
    void getJobsList().then((data) => {
      setJobs(data.filter((job: any) => job.isActive));
      setReady(true);
    });
  }, [isCareerEnabled]);

  if (!ready) return null;
  if (!isCareerEnabled) return <NotFoundPage />;

  return (
    <>
      <Helmet>
        <title>{t('lifeAtSimisi')}</title>
        <meta name="description" content={tLife('subtitle')} />
      </Helmet>
      <PageHero
        title={t('lifeAtSimisi')}
        subtitle={tLife('subtitle')}
        breadcrumbs={[
          { label: t('home'), href: '/' },
          { label: t('career') },
          { label: t('lifeAtSimisi') },
        ]}
      />

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
        <div className="max-w-[1170px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel text={tLife('culture.label')} />
            <h2
              className="text-[32px] md:text-[40px] font-bold text-navy leading-snug mb-6 font-heading"
            >
              {tLife('culture.title')}
            </h2>
            <p className="text-[16px] text-gray-500 leading-relaxed font-sans">
              {tLife('culture.description')}
            </p>
          </div>
          <div className="aspect-[4/3] rounded-lg overflow-hidden border border-light shadow-lg">
            <Image
              src="/images/simisi/page16_img2.png"
              alt="Life at Simisi"
              width={600}
              height={450}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-light/30">
        <div className="max-w-[1170px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="aspect-[4/3] rounded-lg overflow-hidden order-2 lg:order-1 border border-light shadow-lg">
            <Image
              src="/images/simisi/page16_img3.png"
              alt="Simisi Team"
              width={600}
              height={450}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <SectionLabel text={tLife('environment.label')} />
            <h2
              className="text-[32px] md:text-[40px] font-bold text-navy leading-snug mb-6 font-heading"
            >
              {tLife('environment.title')}
            </h2>
            <p className="text-[16px] text-gray-500 leading-relaxed font-sans">
              {tLife('environment.description')}
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
        <div className="max-w-[1170px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel text={tLife('consultant.label')} />
            <h2
              className="text-[32px] md:text-[40px] font-bold text-navy leading-snug mb-6 font-heading"
            >
              {tLife('consultant.title')}
            </h2>
            <p className="text-[16px] text-gray-500 leading-relaxed mb-8 font-sans">
              {tLife('consultant.description')}
            </p>
            <Link
              href="#positions"
              className="inline-flex items-center bg-navy text-white text-[14px] font-semibold uppercase px-[25px] py-[11px] rounded-[25px] hover:bg-primary transition-all duration-300"
            >
              Explore Positions
            </Link>
          </div>
          <div className="aspect-[4/3] rounded-lg overflow-hidden border border-light shadow-lg">
            <Image
              src="/images/simisi/page15_img1.png"
              alt="Life as a Consultant"
              width={600}
              height={450}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section id="positions" className="py-20 md:py-32 px-5 md:px-[40px] bg-light/30">
        <div className="max-w-[1170px] mx-auto">
          <div className="text-center mb-16">
            <SectionLabel text="Join Our Team" className="text-center" />
            <h2
              className="text-[32px] md:text-[45px] font-bold text-navy leading-snug font-heading"
            >
              Open positions
            </h2>
          </div>

          {jobs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {jobs.map((job) => (
                <JobCard
                  key={job.slug}
                  title={job.title}
                  description={job.shortDescription}
                  slug={job.slug}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-16 text-center border border-light">
              <p className="text-gray-500 font-sans italic">No positions currently open. Please check back later or send us your CV to career@simisi.id</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 md:py-32 px-5 md:px-[40px] bg-navy relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="max-w-[800px] mx-auto text-center relative z-10">
          <h2
            className="text-[32px] md:text-[48px] font-bold text-white leading-tight mb-6 font-heading"
          >
            {tLife('cta.title')}
          </h2>
          <p className="text-[18px] text-white/70 leading-relaxed mb-10 font-sans max-w-2xl mx-auto">
            {tLife('cta.description')}
          </p>
          <Link
            href="/contact-us"
            className="inline-flex items-center bg-primary text-white text-[15px] font-bold uppercase px-12 py-5 rounded-full hover:bg-white hover:text-navy transition-all duration-500 shadow-xl shadow-primary/20"
          >
            {tLife('cta.button')}
          </Link>
        </div>
      </section>
    </>
  );
}
